import * as THREE from 'three'
import LeafGeometry from '@/geometries/LeafGeometry'
import { times } from '@/utils'
import { sample, randFloatSpread } from '@/utils/random'

/* eslint-disable */
const defaultSettings = {
  width: 250,      // width of release area (x)
  height: 250,     // height of release area (y)
  depth: 250,      // depth (or length) of release area (z)
  count: 100,      // number of leaves
  size: 0.3,       // leaf size (max)
  colors: [
    0xffffff       // leaf colors
  ],
  gravity: -9.8,   // gravitational pull (y-axis)
  wind: {
    speed: 0,      // wind speed
    rotation: 0    // wind direction (angle)
  },
  variance: {
    rx: 25,         // noise range in x-direction
    ry: 25,         // noise range in y-direction
    rz: 25          // noise range in z-direction
  }
}
/* eslint-enable */

const up = new THREE.Vector3(0, 1, 0)

/**
 * Generates a leaf mesh with specified color.
 * @param {Integer} color hexadecimal triplet format
 */
const createLeaf = color => {
  const geometry = new LeafGeometry()
  const material = new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide })
  return new THREE.Mesh(geometry, material)
}

/**
 * Generates a random rotation.
 */
function randomAngle () {
  return new THREE.Euler(
    randFloatSpread(Math.PI),
    randFloatSpread(Math.PI)
  )
}

/**
 * Generates a vector of random length and direction.
 * @param {Number} rx noise range for x
 * @param {Number} ry noise range for y
 * @param {Number} rz noise range for z
 */
function randomNoise (rx, ry, rz) {
  return new THREE.Vector3(
    randFloatSpread(rx),
    randFloatSpread(ry),
    randFloatSpread(rz)
  )
}

/**
 * Generates a random position in the plane defined by width, height and depth.
 * @param {Number} width width of area (x)
 * @param {Number} height height of area (y)
 * @param {Number} depth depth (or length) of area (z)
 */
function randomPosition (width, height, depth) {
  return new THREE.Vector3(
    randFloatSpread(width),
    randFloatSpread(height),
    randFloatSpread(depth)
  )
}

export default class Leaves extends THREE.Group {
  name = 'Leaves'

  constructor (settings) {
    super()

    this.debug = process.env.NODE_ENV !== 'production'
    this.settings = Object.assign({}, defaultSettings, settings)

    const { gravity, wind } = this.settings
    this.velocity = new THREE.Vector3(wind.speed, gravity, 0)
      .applyAxisAngle(up, wind.rotation)

    this.createLeaves()
    this.debug && this.drawBoundingBox()
  }

  createLeaves () {
    const { width, height, depth, count, size, colors, variance } = this.settings

    times(count, () => {
      const color = sample(colors)
      const leaf = createLeaf(color)

      const scalar = Math.random() * size
      leaf.scale.multiplyScalar(scalar)

      const position = randomPosition(width, height, depth)
      leaf.position.copy(position)

      const angle = randomAngle()
      leaf.rotation.copy(angle)

      const { rx, ry, rz } = variance
      const angularVelocity = new THREE.Vector2(randFloatSpread(5), randFloatSpread(5))
      const velocity = this.velocity.clone().add(randomNoise(rx, ry, rz))
      const target = this.velocity.clone().add(randomNoise(rx, ry, rz))
      const alpha = Math.random() * 0.01

      leaf.userData = {
        angularVelocity,
        velocity,
        target,
        alpha
      }

      this.add(leaf)
    })
  }

  drawBoundingBox () {
    const { width, height, depth } = this.settings
    const geometry = new THREE.BoxGeometry(width, height, depth)
    const material = new THREE.MeshBasicMaterial({ color: 0xffff00, wireframe: true })
    const bbox = new THREE.Mesh(geometry, material)
    bbox.name = 'debug'
    this.add(bbox)
  }

  update (delta) {
    this.children.forEach(mesh => {
      if (mesh.name === 'debug') return

      const { width, height, depth } = this.settings
      const { angularVelocity, velocity, target, alpha } = mesh.userData

      // rotate
      const rotation = angularVelocity.clone().multiplyScalar(delta)
      mesh.rotateX(rotation.x)
      mesh.rotateY(rotation.y)

      // translate
      const translation = velocity.clone().multiplyScalar(delta)
      mesh.position.add(translation)

      // update parameters
      mesh.userData.velocity.lerp(target, alpha)
      mesh.userData.alpha *= 1.1

      // set new velocity target if required
      if (alpha >= 1) {
        const { rx, ry, rz } = this.settings.variance
        mesh.userData.target = this.velocity.clone().add(randomNoise(rx, ry, rz))
        mesh.userData.alpha = Math.random() * 0.01
      }

      // reset position if required
      if (mesh.position.x < -width / 2) mesh.position.x += width
      if (mesh.position.x > width / 2) mesh.position.x -= width
      if (mesh.position.y < -height / 2) mesh.position.y += height
      if (mesh.position.y > height / 2) mesh.position.y -= height
      if (mesh.position.z < -depth / 2) mesh.position.z += depth
      if (mesh.position.z > depth / 2) mesh.position.z -= depth
    })
  }
}
